import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useFormContext, UseFormReturn } from 'react-hook-form'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { WalletSelector } from '@components/Web3/Wallet/WalletSelector'
import { ErrorMessage, FancyButton, FormRender, Stack, Text, TextField } from '@ui'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import {
    Address,
    TransferAssetTransactionContext,
    useMyLinkedWallets,
    useTransferAssetTransaction,
} from 'use-towns-client'
import { formatUnits } from 'viem'

import { tradingChains } from '../../tradingConstants'
import { Disclaimer } from '../../ui/Disclaimer'
import { TransferSuccessCallback } from '../types'
import { checkInvalidAmount, parseTransferAmount } from '../utils'

import { EvmTokenTransfer, evmTokenTransferSchema } from './schema'

export const TransferEVMTokenForm = (props: {
    evmWalletAddress: Address | undefined
    balance: bigint
    symbol: string
    decimals: number
    contractAddress: Address
    onTransferSuccess: TransferSuccessCallback
}) => {
    const { evmWalletAddress, balance, symbol, decimals, contractAddress, onTransferSuccess } =
        props

    const { data: linkedWallets, isLoading: isLinkedWalletsLoading } = useMyLinkedWallets()

    return isLinkedWalletsLoading ? (
        <Stack padding="md">
            <ButtonSpinner />
        </Stack>
    ) : (
        <Stack grow>
            <FormRender
                gap
                grow
                schema={evmTokenTransferSchema}
                id="EvmTokenTransferForm"
                mode="onChange"
            >
                {(form: UseFormReturn<EvmTokenTransfer>) => (
                    <FormProvider {...form}>
                        <Stack grow>
                            <FormContent
                                evmWalletAddress={evmWalletAddress}
                                balance={balance}
                                linkedWallets={linkedWallets}
                                symbol={symbol}
                                decimals={decimals}
                                contractAddress={contractAddress}
                                onTransferSuccess={onTransferSuccess}
                            />
                        </Stack>
                    </FormProvider>
                )}
            </FormRender>
        </Stack>
    )
}

function FormContent(props: {
    evmWalletAddress: Address | undefined
    balance: bigint | undefined
    linkedWallets: ReturnType<typeof useMyLinkedWallets>['data']
    symbol: string
    decimals: number
    contractAddress: Address
    onTransferSuccess: TransferSuccessCallback
}) {
    const {
        evmWalletAddress,
        balance,
        linkedWallets,
        symbol,
        decimals,
        contractAddress,
        onTransferSuccess,
    } = props

    const { register, formState, setValue, handleSubmit, watch, reset } =
        useFormContext<EvmTokenTransfer>()
    const { transferAsset } = useTransferAssetTransaction()
    const amount = watch('amount')
    const recipient = watch('recipient')

    const recipientIsNotInLinkedWallets = useMemo(() => {
        if (!recipient || !linkedWallets) {
            return false
        }
        return !linkedWallets.some((wallet) => wallet.toLowerCase() === recipient?.toLowerCase())
    }, [linkedWallets, recipient])

    const { errors } = formState

    const onMaxClick = useCallback(() => {
        if (!balance) {
            return
        }
        const maxAmount = formatUnits(balance, decimals)

        setValue('amount', maxAmount, { shouldValidate: true })
    }, [balance, setValue, decimals])

    // Always update balance so the schema can do correct validation
    useEffect(() => {
        if (balance !== undefined) {
            setValue('balance', balance, { shouldValidate: false })
        }
    }, [balance, setValue])

    useEffect(() => {
        setValue('decimals', decimals, { shouldValidate: false })
    }, [decimals, setValue])

    const disabled = formState.isSubmitting || !formState.isValid

    const onSend = useCallback(
        async (data: EvmTokenTransfer, getSigner: GetSigner) => {
            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }

            const invalidAmount = checkInvalidAmount({
                value: data.amount,
                balance: data.balance,
                decimals: data.decimals,
            })
            if (invalidAmount) {
                return
            }
            const parsedAmount = parseTransferAmount({
                value: data.amount,
                decimals: data.decimals,
            })
            const recipient = data.recipient

            console.log('[TransferEVMTokenForm] transferAsset', {
                contractAddress,
                amount: parsedAmount,
                recipient,
            })

            const isEthNative =
                contractAddress.toLowerCase() ===
                    tradingChains['8453'].nativeTokenAddress.toLowerCase() &&
                symbol === tradingChains['8453'].tokenSymbol

            let context: TransferAssetTransactionContext | undefined

            if (isEthNative) {
                context = await transferAsset({
                    data: {
                        contractAddress,
                        value: parsedAmount,
                        recipient,
                    },
                    signer,
                })
            } else {
                context = await transferAsset({
                    data: {
                        contractAddress,
                        erc20Amount: parsedAmount,
                        recipient,
                    },
                    signer,
                })
            }

            if (context?.receipt?.status === 1) {
                await onTransferSuccess({
                    amount,
                    symbol,
                    recipient,
                })
                reset()
                popupToast(({ toast }) => (
                    <StandardToast.Success
                        toast={toast}
                        message={`${amount} ${symbol} was sent to ${shortAddress(recipient)}`}
                    />
                ))
            } else {
                console.error(`Failed to send ${amount} ${symbol}:: `, context?.error)
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message={`Failed to send ${amount} ${symbol}`}
                    />
                ))
            }
        },
        [contractAddress, reset, onTransferSuccess, symbol, transferAsset, amount],
    )

    return (
        <Stack grow gap="md">
            <Stack gap>
                <Stack gap="sm">
                    <TextField
                        autoFocus
                        renderLabel={(label) => (
                            <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                                <Text strong>{label}</Text>
                                <Stack
                                    cursor="pointer"
                                    data-testid="max-button"
                                    justifyContent="end"
                                    onClick={onMaxClick}
                                >
                                    <Text size="md" color="cta2">
                                        Enter Max
                                    </Text>
                                </Stack>
                            </Stack>
                        )}
                        label="Amount"
                        after={<Text color="gray2">{symbol}</Text>}
                        background="level2"
                        inputWidth="100%"
                        inputLimit={18}
                        placeholder="0"
                        tone={errors.amount ? 'error' : 'neutral'}
                        {...register('amount')}
                    />
                    <ErrorMessage preventSpace errors={errors} fieldName="amount" />
                </Stack>
                <Stack>
                    <Text strong>Send to</Text>
                    <WalletSelector
                        aaAddress={evmWalletAddress}
                        linkedWallets={linkedWallets}
                        fromWallet={evmWalletAddress}
                        showBalance={symbol === 'ETH'}
                    />
                </Stack>
            </Stack>
            <Stack grow justifyContent="end" gap="sm">
                {recipientIsNotInLinkedWallets && (
                    <Disclaimer>
                        Make sure the wallet you&apos;re sending to supports Base.
                    </Disclaimer>
                )}
                <WalletReady>
                    {({ getSigner }) => (
                        <FancyButton
                            layoutRoot
                            cta
                            compact
                            spinner={formState.isSubmitting}
                            borderRadius="lg"
                            disabled={disabled}
                            onClick={handleSubmit((data) => onSend(data, getSigner))}
                        >
                            {formState.isSubmitting ? `Sending ${amount} ${symbol}` : 'Send'}
                        </FancyButton>
                    )}
                </WalletReady>
            </Stack>
        </Stack>
    )
}
